import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToast } from '../../contexts/ToastContext'

import {
  Drawer,
  DrawerContent,
} from '@progress/kendo-react-layout'

import JobsKendoGrid from '../../components/grids/JobsKendoGrid'
import AddJobForm from '../../components/FormComps/Jobs/AddJobForm'
import EditJobForm from '../../components/FormComps/Jobs/EditJobForm'

import {
  getJobs, 
  completeJob, 
  deleteJob,
} from '../../data/jobData'

import { DateFormatterReverse } from '../../data/helper'

import {
  jobsGrid,
  getSiteTypes,
  dropDown,
} from '../../data/interfaces'

interface JobsKendoStandaloneProps {
  lockEdit?: boolean
}

function JobsKendoStandalone(props: JobsKendoStandaloneProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showToast } = useToast()

  const [data, setData] = useState<{ data: jobsGrid[]; total: number }>({
    data: [],
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [dataState, setDataState] = useState<any>({
    take: 25,
    skip: 0,
  })

  const [showCompleted, setShowCompleted] = useState(false)
  const [showDeleted, setShowDeleted] = useState(false)
  const [showEngineerComments, setShowEngineerComments] = useState(false)
  const [showRemedialWorks, setShowRemedialWorks] = useState(false)

  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null)
  const [addDrawerOpen, setAddDrawerOpen] = useState(false)

  const [clientData, setClientData] = useState<dropDown[]>([])
  const [siteData, setSiteData] = useState<dropDown[]>([])
  const [departmentData, setDepartmentData] = useState<dropDown[]>([])
  const [employeeData, setEmployeeData] = useState<dropDown[]>([])
  const [workTypeData, setWorkTypeData] = useState<dropDown[]>([])
  const [likelihoodData, setLikelihoodData] = useState<dropDown[]>([])
  const [priorityData, setPriorityData] = useState<dropDown[]>([])
  const [enquirySourceData, setEnquirySourceData] = useState<dropDown[]>([])

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        setClientData([])
        setSiteData([])
        setDepartmentData([])
        setEmployeeData([])
        setWorkTypeData([])
        setLikelihoodData([])
        setPriorityData([])
        setEnquirySourceData([])
      } catch (error) {
        console.error('Error loading dropdown data:', error)
        showToast('Error loading form data', 'error')
      }
    }

    loadDropdownData()
  }, [showToast])

  const loadJobs = async (newDataState?: any) => {
    setLoading(true)
    const currentDataState = newDataState || dataState

    try {
      const filterParams = {
        skip: currentDataState.skip,
        take: currentDataState.take,
        showCompleted,
        showDeleted,
      }

      const result = await getJobs(
        showCompleted,
        showDeleted,
        showEngineerComments,
        showRemedialWorks
      )
      
      if (result && Array.isArray(result)) {
        const formattedData = result.map((job: any) => ({
          ...job,
          refFormatted: job.jobRef ? `${job.jobRef}` : '-',
        }))
        
        setData({
          data: formattedData,
          total: result.length,
        })
      }
    } catch (error) {
      console.error('Error loading jobs:', error)
      showToast('Error loading jobs', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadJobs()
  }, [showCompleted, showDeleted])

  const handleDataStateChange = (event: any) => {
    setDataState(event.dataState)
    loadJobs(event.dataState)
  }

  const handleShowCompleted = () => {
    setShowCompleted(!showCompleted)
  }

  const handleShowDeleted = () => {
    setShowDeleted(!showDeleted)
  }

  const handleShowEngineerComments = () => {
    setShowEngineerComments(!showEngineerComments)
  }

  const handleShowRemedialWorks = () => {
    setShowRemedialWorks(!showRemedialWorks)
  }

  const handleAddToggle = () => {
    setAddDrawerOpen(!addDrawerOpen)
  }

  const handleEditToggle = (jobId: number) => {
    setSelectedJobId(jobId)
    setEditDrawerOpen(true)
  }

  const handleCloseEdit = () => {
    setEditDrawerOpen(false)
    setSelectedJobId(null)
  }

  const handleJobUpdate = async (formData: any) => {
    try {
      showToast('Job updated successfully', 'success')
      handleCloseEdit()
      loadJobs()
    } catch (error) {
      console.error('Error updating job:', error)
      showToast('Error updating job', 'error')
    }
  }

  const handleJobComplete = async (jobId: number) => {
    try {
      await completeJob(jobId)
      showToast('Job completed successfully', 'success')
      loadJobs()
    } catch (error) {
      console.error('Error completing job:', error)
      showToast('Error completing job', 'error')
    }
  }

  const handleJobDelete = async (jobId: number) => {
    try {
      await deleteJob(jobId)
      showToast('Job deleted successfully', 'success')
      loadJobs()
    } catch (error) {
      console.error('Error deleting job:', error)
      showToast('Error deleting job', 'error')
    }
  }

  const handleExport = () => {
    showToast('Export functionality coming soon', 'info')
  }

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: '#ffffff'
    }}>
      <JobsKendoGrid
        data={data}
        loading={loading}
        onDataStateChange={handleDataStateChange}
        lockEdit={props.lockEdit || false}
        showCompleted={showCompleted}
        showDeleted={showDeleted}
        showEngineerComments={showEngineerComments}
        showRemedialWorks={showRemedialWorks}
        onShowCompleted={handleShowCompleted}
        onShowDeleted={handleShowDeleted}
        onShowEngineerComments={handleShowEngineerComments}
        onShowRemedialWorks={handleShowRemedialWorks}
        onAddToggle={handleAddToggle}
        onEditToggle={handleEditToggle}
        onJobComplete={handleJobComplete}
        onJobDelete={handleJobDelete}
        onExport={handleExport}
      />

      {editDrawerOpen && (
        <Drawer
          position="end"
          mode="push"
          onOverlayClick={handleCloseEdit}
          style={{ width: '800px' }}
        >
          <DrawerContent style={{ padding: '2rem' }}>
            {selectedJobId && (
              <EditJobForm
                key={selectedJobId}
                onJobId={selectedJobId}
                clientData={clientData}
                siteData={siteData}
                departmentData={departmentData}
                employeeData={employeeData}
                workTypeData={workTypeData}
                likelihoodData={likelihoodData}
                priorityData={priorityData}
                enquirySourceData={enquirySourceData}
                onSubmit={handleJobUpdate}
                onCancel={handleCloseEdit}
                lockEdit={props.lockEdit || false}
              />
            )}
          </DrawerContent>
        </Drawer>
      )}

      {addDrawerOpen && (
        <Drawer
          position="end"
          mode="push"
          onOverlayClick={handleAddToggle}
          style={{ width: '800px' }}
        >
          <DrawerContent style={{ padding: '2rem' }}>
            <AddJobForm
              clientData={clientData}
              siteData={siteData}
              departmentData={departmentData}
              employeeData={employeeData}
              workTypeData={workTypeData}
              likelihoodData={likelihoodData}
              priorityData={priorityData}
              enquirySourceData={enquirySourceData}
              onSubmit={handleJobUpdate}
              onCancel={handleAddToggle}
              lockEdit={props.lockEdit || false}
            />
          </DrawerContent>
        </Drawer>
      )}
    </div>
  )
}

export default JobsKendoStandalone
